/**
 * Pure functions for slicing a CMX Document Content body into per-section
 * fragments, in the order the sections appear in the note.
 *
 * Slicing is always in note order. Template order is a separate step
 * (./note-template `arrangeByTemplate`) applied over these slices, and only
 * when a template resolves for the note AND its sections carry a
 * `section_code`. An L4 payload carries none (CMXDC-LAYER-011 -- `kind` of
 * `section`/`sectionHeader` and nothing else), so an L4 read always renders in
 * note order, which CDMX-20061 permits: "Sections can be in the order they
 * appear in the note."
 *
 * Titles here come from the section header's own surface text, which every
 * payload carries -- as a `sectionHeader` annotation whose range covers the
 * heading. A templated view titles from the section-code registry instead.
 */

import type { ViewAnnotation } from './cmx-document-content-adapter';
import {
  ANNOTATION_KIND_SECTION,
  ANNOTATION_KIND_SECTION_HEADER,
  ATTRIBUTE_KEY_SECTION_CODE
} from './cmx-document-content-types';

export type SectionSlice = {
  title: string;
  /**
   * The section's `section_code`, or `null` for an untyped section -- which is every section in
   * an L4 payload (CMXDC-LAYER-011) and any L5 section its producer could not type (CMXDC-ANN-018).
   */
  sectionCode: string | null;
  headerMarkdown: string;
  bodyMarkdown: string;
  start: number;
  end: number;
  annotation: ViewAnnotation;
};

export type SliceResult = {
  preamble: string | null;
  slices: SectionSlice[];
  unclaimed: { start: number; end: number; text: string }[];
};

export function headingTitle(headerMarkdown: string): string {
  return headerMarkdown
    .trim()
    .replace(/^#{1,6}\s*/, '')
    .replace(/^\*{1,3}/, '')
    .replace(/\*{1,3}$/, '')
    .replace(/\s*#{1,6}$/, '')
    .replace(/<\/?[a-zA-Z][^>]*>/g, '')
    .trim();
}

type HtmlBlockRange = { start: number; end: number };

const HTML_TABLE_TAG = /<\/?table\b[^>]*>/gi;

function findHtmlTableRanges(body: string): HtmlBlockRange[] {
  const ranges: HtmlBlockRange[] = [];
  let depth = 0;
  let blockStart = -1;

  HTML_TABLE_TAG.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = HTML_TABLE_TAG.exec(body)) !== null) {
    if (match[0][1] === '/') {
      depth = Math.max(0, depth - 1);
      if (depth === 0 && blockStart >= 0) {
        ranges.push({ start: blockStart, end: match.index + match[0].length });
        blockStart = -1;
      }
    } else {
      if (depth === 0) {
        blockStart = match.index;
      }
      depth += 1;
    }
  }

  if (depth > 0 && blockStart >= 0) {
    ranges.push({ start: blockStart, end: body.length });
  }

  return ranges;
}

function extendPastHtmlTable(
  boundary: number,
  ranges: HtmlBlockRange[]
): number {
  for (const range of ranges) {
    if (boundary > range.start && boundary < range.end) {
      return range.end;
    }
  }
  return boundary;
}

export type SliceOptions = {
  /**
   * The body is plain text, so a header is its characters as written: there is
   * no Markdown to strip from a title, and a `#` in it is the author's.
   */
  plain?: boolean;
};

export function sliceSectionsFromBody(
  body: string,
  annotations: ViewAnnotation[],
  { plain = false }: SliceOptions = {}
): SliceResult {
  const sections = annotations
    .filter((a) => a.kind === ANNOTATION_KIND_SECTION && a.spans.length > 0)
    .sort((a, b) => a.spans[0].start - b.spans[0].start);

  const headerByParent = new Map<string, ViewAnnotation>();
  for (const a of annotations) {
    if (
      a.kind === ANNOTATION_KIND_SECTION_HEADER &&
      a.parentId &&
      a.spans.length > 0
    ) {
      headerByParent.set(a.parentId, a);
    }
  }

  const htmlTableRanges = plain ? [] : findHtmlTableRanges(body);

  const slices: SectionSlice[] = [];
  for (const section of sections) {
    const header = headerByParent.get(section.id);
    const sectionStart = section.spans[0].start;
    const sectionEnd = extendPastHtmlTable(
      section.spans[0].end,
      htmlTableRanges
    );

    const headerEnd = header
      ? Math.min(Math.max(header.spans[0].end, sectionStart), sectionEnd)
      : sectionStart;

    const headerMarkdown = body.substring(sectionStart, headerEnd);
    const bodyMarkdown = body
      .substring(headerEnd, sectionEnd)
      .replace(/^\n+/, '');

    slices.push({
      title: plain ? headerMarkdown.trim() : headingTitle(headerMarkdown),
      sectionCode:
        section.attributes.find((a) => a.key === ATTRIBUTE_KEY_SECTION_CODE)
          ?.value ?? null,
      headerMarkdown,
      bodyMarkdown,
      start: sectionStart,
      end: sectionEnd,
      annotation: section
    });
  }

  const firstStart = slices.length > 0 ? slices[0].start : body.length;
  const preambleText = body.substring(0, firstStart).trim();

  return {
    preamble: preambleText.length > 0 ? preambleText : null,
    slices,
    unclaimed: findUnclaimed(body, slices)
  };
}

function findUnclaimed(
  body: string,
  slices: SectionSlice[]
): { start: number; end: number; text: string }[] {
  if (slices.length === 0) {
    return [];
  }

  const gaps: { start: number; end: number; text: string }[] = [];
  let cursor = slices[0].start;

  for (const slice of slices) {
    if (slice.start > cursor) {
      pushIfSubstantive(body, cursor, slice.start, gaps);
    }
    cursor = Math.max(cursor, slice.end);
  }

  if (cursor < body.length) {
    pushIfSubstantive(body, cursor, body.length, gaps);
  }

  return gaps;
}

function pushIfSubstantive(
  body: string,
  start: number,
  end: number,
  into: { start: number; end: number; text: string }[]
): void {
  const text = body.substring(start, end);
  if (text.trim().length > 0) {
    into.push({ start, end, text: text.replace(/^\n+|\n+$/g, '') });
  }
}
